import React, { useMemo } from "react";
import { Box, Chip, Tooltip, Typography } from "@mui/material";
import { PolicyInsightsRuleBased, RuleType } from "../../interfaces/DTO/insights/PolicyInsightsRuleBased";
import {
  CATEGORY_ORDER,
  colorRuleType,
  formatRuleType,
  getCategoryColor,
  getCategoryName,
  getRuleTypesByCategory,
} from "../../utils/insightsRuleUtils";

interface Props {
  insights: PolicyInsightsRuleBased[];
  selectedRuleType?: RuleType | null;
  onRuleTypeFilter: (ruleType: RuleType | null) => void;
}

const metricRowSx = {
  color: "#6d1727",
  fontWeight: 500,
};

const InsightsSummaryByCategory: React.FC<Props> = ({ insights, selectedRuleType, onRuleTypeFilter }) => {
  const countsByRuleType = useMemo(
    () =>
      insights.reduce((acc, insight) => {
        acc[insight.rule_type] = (acc[insight.rule_type] || 0) + 1;
        return acc;
      }, {} as Partial<Record<RuleType, number>>),
    [insights]
  );

  const categories = useMemo(
    () =>
      CATEGORY_ORDER.map((category) => {
        const ruleTypes = getRuleTypesByCategory(category)
          .map((ruleType) => ({ ruleType, count: countsByRuleType[ruleType] || 0 }))
          .filter(({ count }) => count > 0)
          .sort((a, b) => b.count - a.count);
        const total = ruleTypes.reduce((sum, { count }) => sum + count, 0);
        return { category, ruleTypes, total };
      }).filter(({ total }) => total > 0),
    [countsByRuleType]
  );

  const total = categories.reduce((sum, { total: t }) => sum + t, 0);
  const hasSelection = selectedRuleType !== null && selectedRuleType !== undefined;

  const clearFilterLink = hasSelection && (
    <Box
      onClick={() => onRuleTypeFilter(null)}
      sx={{ mt: 1, cursor: "pointer", fontSize: 12, color: "text.secondary", textDecoration: "underline", textAlign: "center" }}
    >
      נקה פילטר
    </Box>
  );

  if (total === 0) {
    return (
      <>
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", mt: 2 }}>
          אין תובנות
        </Typography>
        {clearFilterLink}
      </>
    );
  }

  return (
    <Box sx={{ textAlign: "center", px: 2 }}>
      {/* פס יחסי לפי קטגוריה */}
      <Box sx={{ display: "flex", height: 14, borderRadius: 7, overflow: "hidden", mb: 1.5 }}>
        {categories.map(({ category, total: catTotal }) => (
          <Tooltip key={category} title={`${getCategoryName(category)}: ${catTotal}`}>
            <Box sx={{ flex: catTotal, bgcolor: getCategoryColor(category), borderLeft: "2px solid #fff" }} />
          </Tooltip>
        ))}
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {categories.map(({ category, ruleTypes, total: catTotal }) => {
          const color = getCategoryColor(category);
          return (
            <Box key={category}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Chip
                  label={catTotal}
                  size="small"
                  sx={{ bgcolor: color, color: "#fff", fontWeight: 700, borderRadius: "6px", flexShrink: 0 }}
                />
                <Typography variant="body1" sx={{ ...metricRowSx, fontWeight: 700 }}>
                  {getCategoryName(category)}
                </Typography>
              </Box>
              {ruleTypes.map(({ ruleType, count }) => {
                const ruleColor = colorRuleType(ruleType);
                const isSelected = selectedRuleType === ruleType;
                const isDimmed = hasSelection && !isSelected;
                return (
                  <Box
                    key={ruleType}
                    onClick={() => onRuleTypeFilter(isSelected ? null : ruleType)}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      pr: 4.5,
                      cursor: "pointer",
                      borderRadius: 1,
                      opacity: isDimmed ? 0.4 : 1,
                      bgcolor: isSelected ? `${ruleColor}18` : "transparent",
                      transition: "opacity 0.15s",
                      "&:hover": { opacity: 1 },
                    }}
                  >
                    <Typography variant="body2" sx={{ ...metricRowSx, textAlign: "right", fontWeight: isSelected ? 700 : 400 }}>
                      {formatRuleType(ruleType)}
                    </Typography>
                    <Typography variant="body2" sx={{ ...metricRowSx, fontWeight: 700 }}>
                      {count}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          );
        })}
      </Box>

      {clearFilterLink}
    </Box>
  );
};

export default InsightsSummaryByCategory;
